import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Lock } from "lucide-react";
import { api } from "@/lib/api";

export default function Login() {
  const [pw, setPw] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [shake, setShake] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextUrl = params.get("next") || "/";

  useEffect(() => {
    // If already authenticated, bounce out
    api.get("/auth/me").then(
      () => navigate(nextUrl, { replace: true }),
      () => {}
    );
    inputRef.current?.focus();
  }, [nextUrl, navigate]);

  async function submit(e) {
    e?.preventDefault();
    if (!pw || loading) return;
    setLoading(true); setErr("");
    try {
      await api.post("/auth/login", { password: pw });
      navigate(nextUrl, { replace: true });
    } catch (ex) {
      const code = ex?.response?.status;
      const msg =
        code === 429
          ? ex.response?.data?.detail || "Too many attempts — slow down."
          : "Incorrect password.";
      setErr(msg);
      setShake(true);
      setTimeout(() => setShake(false), 520);
      setPw("");
      inputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-6"
      style={{
        background:
          "radial-gradient(circle at 20% 10%, #FFE9A6 0%, #FFD36B 35%, #FFA94D 70%, #F76C1C 100%)",
      }}
      data-testid="login-page"
    >
      <div className="pointer-events-none absolute inset-0 opacity-40"
           style={{
             backgroundImage:
               "radial-gradient(rgba(255,255,255,0.35) 1px, transparent 1px)",
             backgroundSize: "22px 22px",
           }} />
      <form
        onSubmit={submit}
        className={
          "relative z-10 w-full max-w-[420px] rounded-3xl bg-white/85 backdrop-blur-xl " +
          "shadow-[0_20px_60px_rgba(184,80,0,0.25)] border border-white/60 " +
          "p-8 sm:p-10 flex flex-col gap-6 transition-transform " +
          (shake ? " animate-[wiggle_0.52s_ease-in-out]" : "")
        }
        data-testid="login-form"
        style={{ animation: shake ? "wiggle 0.52s" : undefined }}
      >
        <style>{`
          @keyframes wiggle {
            0%,100% { transform: translateX(0); }
            15% { transform: translateX(-9px); }
            30% { transform: translateX(9px); }
            45% { transform: translateX(-6px); }
            60% { transform: translateX(6px); }
            75% { transform: translateX(-3px); }
          }
        `}</style>

        <div className="flex flex-col items-center gap-3">
          <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-md">
            <span className="text-white text-3xl" aria-hidden>☀</span>
          </div>
          <h1 className="text-2xl font-black tracking-tight text-stone-900"
              data-testid="login-title">
            Dom's Journey
          </h1>
          <p className="text-sm text-stone-600 -mt-1">Content Studio</p>
        </div>

        <label className="text-sm font-medium text-stone-700 -mb-2"
               htmlFor="pw">Password</label>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
          <input
            id="pw"
            ref={inputRef}
            type={show ? "text" : "password"}
            autoComplete="current-password"
            autoFocus
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="Enter password"
            disabled={loading}
            className="w-full rounded-xl border border-stone-300 bg-white pl-10 pr-11 py-3 text-base outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition disabled:opacity-60"
            data-testid="login-password-input"
          />
          <button
            type="button"
            aria-label={show ? "Hide password" : "Show password"}
            onClick={() => setShow((s) => !s)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-800"
            data-testid="login-password-toggle"
          >
            {show ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>

        {err && (
          <div role="alert"
               className="text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
               data-testid="login-error">
            {err}
          </div>
        )}

        <button
          type="submit"
          disabled={loading || !pw}
          className="w-full rounded-xl py-3 px-5 text-base font-bold text-white bg-gradient-to-br from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-orange-500/25 transition-all active:scale-[0.98]"
          data-testid="login-submit-button"
        >
          {loading ? "Signing in…" : "Sign in"}
        </button>

        <p className="text-xs text-stone-500 text-center">
          Powered by <span className="font-semibold">Dom Studio</span>
        </p>
      </form>
    </div>
  );
}
